import { RequestUtil } from '../../common';
import { AbstractEntrustWebPushProvisionning } from '../abstract-web-push-provisioning';
import { Message, MessageOutput, WPPError } from '../common-objects';
import type {
    EntrustWPPIssuerConfig,
    EntrustWPPConfig,
    OnResult,
    OnFailure,
    OnCancel,
    GetPushOpaqueData,
} from '../common-objects';
import { mapAppleStatusToMessage } from './apple-status-mapping';
import { mapLocaleToAppleFolder } from './locale-mapping';

export class EntrustWebPushProvisionningApple extends AbstractEntrustWebPushProvisionning {
    imgName = 'Add_to_Apple_Wallet.svg';
    imgStyle = ' height: 48px; width: auto; margin:5px; cursor: pointer;';

    constructor(
        config: EntrustWPPConfig,
        issuerConfig: EntrustWPPIssuerConfig,
        getPushOpaqueDataPromise: GetPushOpaqueData,
        onResult: OnResult,
        onFailure: OnFailure,
        onCancel?: OnCancel
    ) {
        super(config, issuerConfig, getPushOpaqueDataPromise, onResult, onFailure, onCancel);
    }

    postInit(): void {
        (window as any).initAddToAppleWallet({
            partnerId: this.config.apple?.partnerId ? this.config.apple.partnerId : this.issuerConfig.partnerId,
            domain:
                this.issuerConfig.environment === 'production'
                    ? 'https://apple-pay.apple.com'
                    : 'https://apple-pay-cert.apple.com',
            buttonId: this.buttonId,
            jwsResolver: this.jwtResolverCallback.bind(this),
            resultResolver: this.resultResolverCallback.bind(this),
        });
    }

    async jwtResolverCallback(payload?: any): Promise<any> {
        // generate walletOpaqueData and complete it with the payload received from Apple
        const walletOpaqueData = await this.generateWalletOpaqueData();
        const pushOpaqueDataResponse = (await this.getPushOpaqueDataPromise(walletOpaqueData)) as any;
        const decryptedData = await this.decryptPushOpaqueData(pushOpaqueDataResponse.pushOpaqueData as string);
        const pushOpaqueDataDecrypted = JSON.parse(new TextDecoder().decode(decryptedData.plaintext));
        if (!pushOpaqueDataDecrypted?.applePay?.token) {
            this.onFailure(new WPPError(Message.TECHNICAL_ERROR));
            return;
        }
        // jws.header arrives as a JSON-encoded string within the token; the reviver parses it inline
        pushOpaqueDataDecrypted.applePay.token = pushOpaqueDataDecrypted.applePay.token.replace(
            'protectedHeader',
            'protectedHeader'
        );
        const parsedToken = JSON.parse(pushOpaqueDataDecrypted.applePay.token, (key, value) =>
            key === 'header' && typeof value === 'string' ? JSON.parse(value) : value
        );
        return parsedToken;
    }

    async resultResolverCallback(result: any): Promise<void> {
        const message = mapAppleStatusToMessage(result.status);
        switch (message) {
            case MessageOutput.OK:
            case MessageOutput.PARTIAL:
            case MessageOutput.NOT_CONFIRMED:
                this.onResult(message);
                break;
            case MessageOutput.CANCELED:
            case MessageOutput.LOGIN_FAILED:
            case MessageOutput.TIMEOUT:
            case MessageOutput.NO_ELIGIBLE_DEVICE:
                this.onCancel?.(message);
                break;
            default:
                this.onFailure(message);
                break;
        }
    }

    getScriptName(): string {
        return 'initAddToAppleWallet.js';
    }

    getWalletName(): string {
        return 'APPLE_PAY';
    }

    generateButton(): void {
        const folder = this.getAppleWalletBadgeFolder();
        const container = this.config.target as HTMLElement;
        //always remove existing button before adding a new one (in case of re-rendering)
        document.getElementById(this.buttonId)?.remove();
        const imgBtn = document.createElement('img');
        const imgBtnSrc = RequestUtil.getResourceUrl(
            'web-push-provisioning.js',
            `images/apple/${folder}/${this.imgName}`,
            'data-wpp'
        );
        imgBtn.setAttribute('src', imgBtnSrc);
        imgBtn.setAttribute('id', this.buttonId);
        imgBtn.setAttribute('style', this.imgStyle);
        container.appendChild(imgBtn);
    }

    completeWalletOpaqueData() {
        return {
            applePay: {
                clientLocale: this.getLocale(),
                cardTemplateIdentifier: this.config.apple?.cardTemplateIdentifier,
            },
        };
    }

    /**
     * Maps browser locale to Apple Wallet badge folder code
     * @returns Folder code (e.g., 'FR', 'US_UK', 'ESMX')
     */
    private getAppleWalletBadgeFolder(): string {
        const locale = this.getLocale();
        return mapLocaleToAppleFolder(locale);
    }
}
